import { PeriodArgs } from 'components/StatsPeriodSelector';
import React, { useContext, useMemo } from 'react';
import {
  usePromoCampaignBudgetMetrics,
  usePromoByDayMetrics,
  usePromoByDomainCompleteMetrics,
  usePromoByDomainTopSitesMetrics,
  usePromoEpisodeLevelMetrics,
} from './state';
import { AdvertisersDashboardContextType, SelectedCampaignSids } from './types';

const AdvertisersDashboardContext = React.createContext<
  AdvertisersDashboardContextType | undefined
>(undefined);

export const useAdvertisersDashboard = () => {
  const context = useContext(AdvertisersDashboardContext);

  if (context === undefined) {
    throw new Error(
      'useAdvertisersDashboard must be used within AdvertisersDashboardProvider',
    );
  }

  return context;
};

interface AdvertisersDashboardProviderProps {
  periodArgs: PeriodArgs;
  campaignIds: SelectedCampaignSids;
}

const AdvertisersDashboardProvider: React.FunctionComponent<
  AdvertisersDashboardProviderProps
> = (props) => {
  const { children, periodArgs, campaignIds } = props;

  const {
    data: dailyStatsData = [],
    isError: dailyStatsError,
    isLoading: dailyStatsLoading,
  } = usePromoByDayMetrics(
    {
      endMillis: periodArgs.endMillis,
      startMillis: periodArgs.startMillis,
      campaignSids: campaignIds.includes('All') ? undefined : campaignIds,
    },
    {
      keepPreviousData: true,
    },
  );

  const {
    data: domainTopSitesStatsData = [],
    isError: domainTopSitesStatsError,
    isLoading: domainTopSitesStatsLoading,
  } = usePromoByDomainTopSitesMetrics(
    {
      endMillis: periodArgs.endMillis,
      startMillis: periodArgs.startMillis,
      campaignSids: campaignIds.includes('All') ? undefined : campaignIds,
    },
    {
      keepPreviousData: true,
    },
  );

  const {
    data: domainCompleteStatsData = [],
    isError: domainCompleteStatsError,
    isLoading: domainCompleteStatsLoading,
  } = usePromoByDomainCompleteMetrics(
    {
      endMillis: periodArgs.endMillis,
      startMillis: periodArgs.startMillis,
      campaignSids: campaignIds.includes('All') ? undefined : campaignIds,
    },
    {
      keepPreviousData: true,
    },
  );

  const {
    data: episodeLevelStatsData = [],
    isError: episodeLevelStatsError,
    isLoading: episodeLevelStatsLoading,
  } = usePromoEpisodeLevelMetrics(
    {
      endMillis: periodArgs.endMillis,
      startMillis: periodArgs.startMillis,
      campaignSids: campaignIds.includes('All') ? undefined : campaignIds,
    },
    {
      keepPreviousData: true,
    },
  );

  const {
    data: campaignBudgetStatsData,
    isError: campaignBudgetStatsError,
    isLoading: campaignBudgetStatsLoading,
  } = usePromoCampaignBudgetMetrics(
    {
      campaignSids: campaignIds.includes('All') ? undefined : campaignIds,
    },
    {
      keepPreviousData: true,
    },
  );

  return (
    <AdvertisersDashboardContext.Provider
      value={useMemo(
        () => ({
          campaignIds,
          campaignBudget: {
            data: campaignBudgetStatsData,
            error: campaignBudgetStatsError,
            loading: campaignBudgetStatsLoading,
          },
          dailyStats: {
            data: dailyStatsData,
            error: dailyStatsError,
            loading: dailyStatsLoading,
          },
          domainCompleteStats: {
            data: domainCompleteStatsData,
            error: domainCompleteStatsError,
            loading: domainCompleteStatsLoading,
          },
          domainTopSiteStats: {
            data: domainTopSitesStatsData,
            error: domainTopSitesStatsError,
            loading: domainTopSitesStatsLoading,
          },
          episodeLevelStats: {
            data: episodeLevelStatsData,
            error: episodeLevelStatsError,
            loading: episodeLevelStatsLoading,
          },
          periodArgs,
        }),
        [
          campaignIds,
          campaignBudgetStatsData,
          campaignBudgetStatsError,
          campaignBudgetStatsLoading,
          dailyStatsData,
          dailyStatsError,
          dailyStatsLoading,
          domainCompleteStatsData,
          domainCompleteStatsError,
          domainCompleteStatsLoading,
          domainTopSitesStatsData,
          domainTopSitesStatsError,
          domainTopSitesStatsLoading,
          episodeLevelStatsData,
          episodeLevelStatsError,
          episodeLevelStatsLoading,
          periodArgs,
        ],
      )}
    >
      {children}
    </AdvertisersDashboardContext.Provider>
  );
};

export default AdvertisersDashboardProvider;
